import React from 'react';
import { Link } from 'react-router-dom';
import { Settings, PanelLeftOpen, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { useSidebarStore } from 'store/sidebar.store';
import { useCommandPaletteStore } from 'store/commandPalette';
import { PERMISSIONS } from 'modules/auth/permissions';
import * as AuthModule from 'modules/auth';
import { useSettingsDialog } from 'components/settings/useSettingsDialog';
import LogoMark from './LogoMark';
import { useStudioNavGroups } from './LeftSidebarContent';

// =============================================================================
// Left Sidebar Rail — the collapsed 68px icon rail. Light surface matching the
// expanded sidebar: gray icons, the current item filled ink. Renders the SAME
// nav destinations as the expanded sidebar (via the shared useStudioNavGroups
// hook), icon-only.
// =============================================================================

export const LeftSidebarRail: React.FC = () => {
  const { t } = useTranslation();
  const { toggleOpen } = useSidebarStore();
  const { groups } = useStudioNavGroups();
  const { open: openSettings } = useSettingsDialog();
  const {
    state: { permissions },
  } = AuthModule.Context.useAuthContext();

  // One flat, permission-filtered icon list (groups already filtered).
  const items = groups.flatMap((g) => g.items);

  return (
    <div className="group/rail flex h-full flex-col items-center gap-1.5 bg-background py-3">
      {/* Brand mark — shows the Examing logo; on hover over the collapsed rail
          or the button itself, it swaps to an expand affordance. Click expands the sidebar. */}
      <button
        type="button"
        onClick={toggleOpen}
        aria-label={t('expand_sidebar')}
        title={t('expand_sidebar')}
        className="group relative mb-2 flex h-9 w-9 items-center justify-center rounded-lg transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="flex items-center justify-center transition-opacity duration-200 [.group:hover_&]:opacity-0 [[data-sidebar=rail]:hover_&]:opacity-0">
          <LogoMark size={30} />
        </span>
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-200 [.group:hover_&]:opacity-100 [[data-sidebar=rail]:hover_&]:opacity-100">
          <PanelLeftOpen
            className="h-[18px] w-[18px] text-muted-foreground transition-colors [.group:hover_&]:text-foreground [[data-sidebar=rail]:hover_&]:text-foreground"
            aria-hidden
          />
        </span>
      </button>

      {/* Search — opens the ⌘K palette (collapsed-rail parity with the pill). */}
      <button
        onClick={() => useCommandPaletteStore.getState().setOpen(true)}
        aria-label={t('cmdk.search_placeholder')}
        title={t('cmdk.search_placeholder')}
        className="mb-1 flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        <Search className="h-4 w-4" aria-hidden />
      </button>

      {/* Nav icons */}
      <nav className="flex flex-col items-center gap-1.5">
        {items.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            title={item.label}
            aria-label={item.label}
            aria-current={item.active ? 'page' : undefined}
            className={cn(
              'relative flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
              item.active
                ? 'bg-foreground text-background'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            <item.icon className="h-4 w-4" strokeWidth={1.75} aria-hidden />
            {item.badge != null && item.badge > 0 && (
              <span className="absolute right-0.5 top-0.5 inline-flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-error-600 px-1 text-[8px] font-bold leading-none text-white">
                {item.badge}
              </span>
            )}
          </Link>
        ))}
      </nav>

      <div className="flex-1" />

      {/* Org settings (owner/admin). Opens the app-wide settings dialog in place
          (?settings=org on the current page) — mirrors the expanded sidebar and
          command palette, not the legacy /app/admin/organization route. */}
      {permissions.includes(PERMISSIONS.ROLES_EDIT) && (
        <button
          type="button"
          onClick={() => openSettings('org')}
          title={t('sidebar_orgsettings')}
          aria-label={t('sidebar_orgsettings')}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <Settings className="h-4 w-4" aria-hidden />
        </button>
      )}
    </div>
  );
};

export default LeftSidebarRail;
